import {
  createDeviceAPIClient,
  createEcoScoreApiClient,
  createExportAPIClient,
  createFuelStopsAPIClient,
  createImportAPIClient,
  createTripAPIClient,
  createTriplogAPIClient,
  createVehicleAPIClient,
} from '@api/MyTripsAPIClientFactory'
import { createFitmentAPIClient } from '@api/VcfgAPIClientFactory'
import { FilterOptions, LoadState } from '@util/types'
import { findEarliestTripInList, findLatestTripInList } from '@util/tripUtils'

// eslint-disable-next-line no-unused-vars
import { ErrorType, FuelStop, FuelUnit, GetTrip, Trip, TripError, TripLog, TripsPage } from '@/generated-clients/myTrips'
import { findClosestZoneContainingLocation } from '@util/zoneService'
// eslint-disable-next-line no-unused-vars
import { AxiosResponse } from 'axios'

const triplogApiClient = createTriplogAPIClient() // eslint-disable-next-line no-unused-vars
const ecoScoreApiClient = createEcoScoreApiClient() // eslint-disable-next-line no-unused-vars

function dispatchResetCarHealth (dispatch) {
  dispatch('CarHealth/resetCarHealth', {}, { root: true })
}

function dispatchResetStatistics (getters, dispatch, vehicleId) {
  const filters: FilterOptions = getters.getFilterOptions

  dispatch('Statistic/resetStatistics', {}, { root: true })
  dispatch('fetchVehicleSummary', { vehicleId, filterFrom: filters.date.from, filterUntil: filters.date.until, filterTagId: filters.tagId, filterPilotId: filters.pilotId, filterZoneId: filters.zoneId })
}

function getEdgeTrip (vehicleId: number, edge: 'latest' | 'oldest') {
  const order = edge === 'latest' ? 'desc' : 'asc'

  return createTripAPIClient().getTrips(
    vehicleId,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    'startTimestamp',
    order,
    0,
    1
  )
}

function removeFuelStop (trip: Trip, fuelStop: FuelStop): Trip {
  const fuelStopsRemoved = trip.fuelStops.filter(fs => fs.id !== fuelStop.id)
  return { ...trip, fuelStops: fuelStopsRemoved }
}

function putFuelStop (trip: Trip, fuelStop: FuelStop): Trip {
  const fuelStopIndex = trip.fuelStops.findIndex(fs => fs.id === fuelStop.id)
  const clonedFuelStops = [...trip.fuelStops] // copy since splice is mutable

  if (fuelStopIndex !== -1) {
    clonedFuelStops[fuelStopIndex] = fuelStop
  } else {
    const insertFuelStopAtIndex = trip.fuelStops.findIndex(fs => fs.timestamp > fuelStop.timestamp)
    if (insertFuelStopAtIndex !== -1) {
      clonedFuelStops.splice(insertFuelStopAtIndex, 0, fuelStop)
    } else {
      clonedFuelStops.push(fuelStop)
    }
  }

  return { ...trip, fuelStops: clonedFuelStops }
}

function putTrip (tripList: Trip[], trip: Trip, moreTripsAvailable: boolean = true): Trip[] {
  const tripIndex = tripList.findIndex(t => t.id === trip.id)
  const clonedTrips = [...tripList]

  trip.fuelStops.forEach(fuelStop => {
    const idx = tripList.findIndex(
      t => t.fuelStops.some(fs => fs.id === fuelStop.id)
    )

    if (idx !== -1) {
      const tripWithoutFuelStop = removeFuelStop(clonedTrips[idx], fuelStop)
      clonedTrips[idx] = tripWithoutFuelStop
    }
  })

  if (tripIndex !== -1) {
    clonedTrips[tripIndex] = trip
  } else {
    const insertTripAtIndex = tripList.findIndex(t => trip.startInfo.timestamp > t.startInfo.timestamp)
    if (insertTripAtIndex !== -1) {
      clonedTrips.splice(insertTripAtIndex, 0, trip)
    } else if (clonedTrips.length === 0 || !moreTripsAvailable) {
      clonedTrips.push(trip)
    }
  }

  return clonedTrips
}

function updateTripEdges (state, trip: Trip) {
  const latestTrip = state.getLatestTripRequest.response
  if (!latestTrip || latestTrip.id === trip.id || latestTrip.startInfo.timestamp < trip.startInfo.timestamp) {
    state.getLatestTripRequest.response = trip
  }

  const oldestTrip = state.getOldestTripRequest.response
  if (!oldestTrip || oldestTrip.id === trip.id || oldestTrip.startInfo.timestamp > trip.startInfo.timestamp) {
    state.getOldestTripRequest.response = trip
  }
}

function hasTriplogChanged (selectedTriplog: TripLog | undefined, vehicleId: number): boolean {
  return selectedTriplog && selectedTriplog.vehicle.id !== vehicleId
}

function createState () {
  return {
    selectedTriplog: null as TripLog,
    vehiclesRequest: {
      status: LoadState.None,
      response: [],
    },
    createTripRequest: {
      status: LoadState.None,
      response: [],
    },
    updateTripRequest: {
      status: LoadState.None,
      response: [],
    },
    mergeTripRequest: {
      status: LoadState.None,
      response: [],
    },
    deleteTripRequest: {
      status: LoadState.None,
      response: [],
    },
    getSingleTripRequest: {
      status: LoadState.None,
      response: {},
    },
    getLatestTripRequest: {
      status: LoadState.None,
      response: null
    },
    getOldestTripRequest: {
      status: LoadState.None,
      response: null
    },
    exportTripsRequest: {
      status: LoadState.None,
      response: [],
    },
    getTripErrorsRequest: {
      status: LoadState.None,
      response: [] as TripError[]
    },
    getTripsRequest: {
      status: LoadState.None,
      trips: [],
      offset: 0,
      limit: 20,
      areMoreTripsAvailable: true,
      filterOptions: FilterOptions.emptyFilters(),
    },
    getVehicleSummaryRequest: {
      status: LoadState.None,
      response: [],
    },
    getFuelStopRequest: {
      status: LoadState.None,
      response: {
        litres: null,
        totalPrice: null,
        address: {
          moniker: '',
          areaCode: '',
          stateRegion: '',
          country: '',
          locality: '',
          streetLine: ''
        },
        location: {
          longitude: null,
          latitude: null
        },
        pilot: null,
        timestamp: ''
      } as FuelStop
    },
    createFuelStopRequest: {
      status: LoadState.None,
      response: {}
    },
    updateFuelStopRequest: {
      status: LoadState.None,
      response: {}
    },
    deleteFuelStopRequest: {
      status: LoadState.None,
      response: {}
    },
    importTripsRequest: {
      status: LoadState.None,
      response: [],
    },
    getDeviceRequest: {
      status: LoadState.None,
      response: {},
    },
    getFitmentRequest: {
      status: LoadState.None,
      response: {},
    },
    updateVehicleSetupRequest: {
      status: LoadState.None,
      response: {}
    },
    getSmartConnectVehiclesRequest: {
      status: LoadState.None,
      response: {}
    },
    setSeedOdometerRequest: {
      status: LoadState.None,
      response: {}
    },
    setFuelConsumptionTypeRequest: {
      status: LoadState.None,
      response: {}
    },
    setFuelUnitRequest: {
      status: LoadState.None,
      response: {}
    },
    setFuelCapacityRequest: {
      status: LoadState.None,
      response: {}
    },
    setFuelStopSuggestionsRequest: {
      status: LoadState.None,
      response: {}
    },
    setMergeBreakThresholdRequest: {
      status: LoadState.None,
      response: {}
    },
    getSuggestedFuelStopsRequest: {
      status: LoadState.None,
      response: []
    },
    hideSuggestedFuelStop: {
      status: LoadState.None,
      response: {}
    },
    showAllSuggestedFuelStops: {
      status: LoadState.None,
      response: {}
    },
    getEcoScoreRecommendationRequest: {
      status: LoadState.None,
      response: {}
    }
  }
}

export default {
  namespaced: true,

  state: createState,

  mutations: {
    SELECT_TRIPLOG (state, triplog) {
      state.selectedTriplog = triplog
    },
    VEHICLES_REQUEST (state) {
      state.vehiclesRequest.status = LoadState.Loading
    },
    VEHICLES_RESPONSE (state, payload) {
      state.vehiclesRequest.status = LoadState.Success
      state.vehiclesRequest.response = payload
    },
    VEHICLES_ERROR_RESPONSE (state, payload) {
      state.vehiclesRequest.status = LoadState.Error
      state.vehiclesRequest.response = payload
    },
    SET_TRIPLOG_VEHICLE_SETTINGS_PROPERTY (state, payload) {
      state.vehiclesRequest.response = state.vehiclesRequest.response
        .map(triplog => {
          if (triplog.vehicle.id === payload.vehicleId) {
            return { ...triplog, vehicleSettings: { ...triplog.vehicleSettings, [payload.name]: payload[payload.name] } }
          } else {
            return triplog
          }
        })

      state.selectedTriplog.vehicleSettings[payload.name] = payload[payload.name]
    },
    CREATE_TRIP_REQUEST (state) {
      state.createTripRequest.status = LoadState.Loading
    },
    CREATE_TRIP_RESPONSE (state, payload) {
      state.createTripRequest.status = LoadState.Success
      state.createTripRequest.response = payload
    },
    CREATE_TRIP_ERROR_RESPONSE (state, payload) {
      state.createTripRequest.status = LoadState.Error
      state.createTripRequest.response = payload
    },
    UPDATE_TRIP_REQUEST (state) {
      state.updateTripRequest.status = LoadState.Loading
    },
    UPDATE_TRIP_RESPONSE (state, payload) {
      state.updateTripRequest.status = LoadState.Success
      state.updateTripRequest.response = payload
    },
    UPDATE_TRIP_ERROR_RESPONSE (state, payload) {
      state.updateTripRequest.status = LoadState.Error
      state.updateTripRequest.response = payload
    },
    DELETE_TRIP_REQUEST (state) {
      state.deleteTripRequest.status = LoadState.Loading
    },
    DELETE_TRIP_RESPONSE (state, payload) {
      state.deleteTripRequest.status = LoadState.Success
      state.deleteTripRequest.response = payload
    },
    DELETE_TRIP_ERROR_RESPONSE (state, payload) {
      state.deleteTripRequest.status = LoadState.Error
      state.deleteTripRequest.response = payload
    },
    GET_SINGLE_TRIP_REQUEST (state) {
      state.getSingleTripRequest.status = LoadState.Loading
    },
    GET_SINGLE_TRIP_RESPONSE (state, payload: Trip) {
      state.getSingleTripRequest.status = LoadState.Success
      state.getSingleTripRequest.response = payload

      const tripExistsInList = state.getTripsRequest.trips.some(t => t.id === payload.id)
      if (tripExistsInList) {
        state.getTripsRequest.trips = putTrip(state.getTripsRequest.trips, payload)
      }

      updateTripEdges(state, payload)
    },
    GET_SINGLE_TRIP_ERROR_RESPONSE (state, payload) {
      state.getSingleTripRequest.status = LoadState.Error
      state.getSingleTripRequest.response = payload
    },
    GET_LATEST_TRIP_REQUEST (state) {
      state.getLatestTripRequest.status = LoadState.Loading
    },
    GET_LATEST_TRIP_RESPONSE (state, payload) {
      state.getLatestTripRequest.status = LoadState.Success
      state.getLatestTripRequest.response = payload
    },
    GET_LATEST_TRIP_ERROR_RESPONSE (state, payload) {
      state.getLatestTripRequest.status = LoadState.Error
      state.getLatestTripRequest.response = payload
    },
    GET_OLDEST_TRIP_REQUEST (state) {
      state.getOldestTripRequest.status = LoadState.Loading
    },
    GET_OLDEST_TRIP_RESPONSE (state, payload) {
      state.getOldestTripRequest.status = LoadState.Success
      state.getOldestTripRequest.response = payload
    },
    GET_OLDEST_TRIP_ERROR_RESPONSE (state, payload) {
      state.getOldestTripRequest.status = LoadState.Error
      state.getOldestTripRequest.response = payload
    },
    EXPORT_TRIPS_REQUEST (state) {
      state.exportTripsRequest.status = LoadState.Loading
    },
    EXPORT_TRIPS_RESPONSE (state, payload) {
      state.exportTripsRequest.status = LoadState.Success
      state.exportTripsRequest.response = payload
    },
    EXPORT_TRIPS_ERROR_RESPONSE (state, payload) {
      state.exportTripsRequest.status = LoadState.Error
      state.exportTripsRequest.response = payload
    },
    GET_TRIP_ERRORS_REQUEST (state) {
      state.getTripErrorsRequest.status = LoadState.Loading
    },
    GET_TRIP_ERRORS_RESPONSE (state, payload: TripError[]) {
      state.getTripErrorsRequest.status = LoadState.Success
      state.getTripErrorsRequest.response = payload
    },
    APPEND_GET_TRIP_ERRORS_RESPONSE (state, payload: TripError[]) {
      state.getTripErrorsRequest.status = LoadState.Success
      Array.isArray(state.getTripErrorsRequest.response)
        ? state.getTripErrorsRequest.response.push(...payload)
        : state.getTripErrorsRequest.response = payload
    },
    GET_TRIP_ERRORS_ERROR_RESPONSE (state, payload) {
      state.getTripErrorsRequest.status = LoadState.Error
      state.getTripErrorsRequest.response = payload
    },
    RESET_GET_TRIPS (state) { // note it does not reset the filters
      state.getTripsRequest.status = LoadState.None
      state.getTripsRequest.trips = []
      state.getTripsRequest.offset = 0
      state.getTripsRequest.limit = 20
      state.getTripsRequest.areMoreTripsAvailable = true
    },
    SET_FILTER_OPTIONS (state, filterOptions) {
      state.getTripsRequest.filterOptions = filterOptions
    },
    MERGE_TRIP_REQUEST (state) {
      state.mergeTripRequest.status = LoadState.Loading
    },
    MERGE_TRIP_RESPONSE (state, { response, firstTripId, secondTripId }) {
      state.mergeTripRequest.status = LoadState.Success
      state.mergeTripRequest.response = response

      const replaceAtIndex = state.getTripsRequest.trips.findIndex(trip => trip.id === firstTripId)

      const clonedTrips = [...state.getTripsRequest.trips] // copy since splice is mutable
      clonedTrips.splice(replaceAtIndex, 2, { ...response, recentlyMerged: true })
      setTimeout(() => {
        state.getTripsRequest.trips.filter(t => t.id === response.id).forEach(t => { t.recentlyMerged = false })
      }, 5000)

      state.getTripsRequest.trips = clonedTrips
      state.getTripsRequest.offset -= 1

      if (state.getTripErrorsRequest.status === LoadState.Success) {
        state.getTripErrorsRequest.response = state.getTripErrorsRequest.response
          .filter(te => (te.first.id !== firstTripId || te.second.id !== secondTripId) && (te.first.id !== secondTripId || te.second.id !== firstTripId)) // errors without merged trips errors only
          .map(te => {
            if (te.first.id === firstTripId || te.first.id === secondTripId) {
              return { ...te, first: response }
            }

            if (te.second.id === firstTripId || te.second.id === secondTripId) {
              return { ...te, second: response }
            }

            return te
          })
      }

      const latestTripId = state.getLatestTripRequest.response && state.getLatestTripRequest.response.id
      if (latestTripId === firstTripId || latestTripId === secondTripId) {
        state.getLatestTripRequest.response = response
      }

      const oldestTripId = state.getOldestTripRequest.response && state.getOldestTripRequest.response.id
      if (oldestTripId === firstTripId || oldestTripId === secondTripId) {
        state.getOldestTripRequest.response = response
      }
    },
    MERGE_TRIP_ERROR_RESPONSE (state, payload) {
      state.mergeTripRequest.status = LoadState.Error
      state.mergeTripRequest.response = payload
    },
    ADD_TRIP (state, trip) {
      state.getTripsRequest.trips = putTrip(state.getTripsRequest.trips, trip, state.getTripsRequest.areMoreTripsAvailable)
      state.getTripsRequest.offset += 1
      updateTripEdges(state, trip)
    },
    REPLACE_TRIP (state, trip) {
      state.getTripsRequest.trips = putTrip(state.getTripsRequest.trips, trip)
      updateTripEdges(state, trip)
    },
    REMOVE_TRIP (state, tripId) {
      const removeAtIndex = state.getTripsRequest.trips
        .findIndex(storedTrip => storedTrip.id === tripId)

      if (removeAtIndex !== -1 && removeAtIndex !== 0 && removeAtIndex !== state.getTripsRequest.trips.length - 1 && state.getTripErrorsRequest.status === LoadState.Success) {
        const first = state.getTripsRequest.trips[removeAtIndex - 1]
        const second = state.getTripsRequest.trips[removeAtIndex + 1]

        if ((first.startInfo.odometer - (second.endInfo && second.endInfo.odometer)) > 1000) {
          state.getTripErrorsRequest.response = [
            ...state.getTripErrorsRequest.response,
            {
              first,
              second,
              type: ErrorType.GapOdometer
            }
          ]
        }

        state.getTripErrorsRequest.response = state.getTripErrorsRequest.response.filter(te => te.first.id !== tripId && te.second.id !== tripId)
      }

      if (removeAtIndex !== -1) {
        const clonedTrips = [...state.getTripsRequest.trips] // copy since splice is mutable
        clonedTrips.splice(removeAtIndex, 1)

        state.getTripsRequest.trips = clonedTrips
        state.getTripsRequest.offset -= 1
      }
    },
    SET_GET_TRIPS_STATUS (state, payload) {
      state.getTripsRequest.status = payload
    },
    NEXT_TRIP_REQUEST (state, payload) {
      state.getTripsRequest.status = payload.status
      state.getTripsRequest.trips = state.getTripsRequest.trips.concat(payload.trips)
      state.getTripsRequest.areMoreTripsAvailable = payload.areMoreTripsAvailable
      state.getTripsRequest.offset += state.getTripsRequest.limit
    },
    SET_TRIPS (state, payload) {
      state.getTripsRequest.trips = payload
    },
    GET_VEHICLE_SUMMARY_REQUEST (state) {
      state.getVehicleSummaryRequest.status = LoadState.Loading
    },
    GET_VEHICLE_SUMMARY_RESPONSE (state, payload) {
      state.getVehicleSummaryRequest.status = LoadState.Success
      state.getVehicleSummaryRequest.response = payload
    },
    GET_VEHICLE_SUMMARY_ERROR_RESPONSE (state, payload) {
      state.getVehicleSummaryRequest.status = LoadState.Error
      state.getVehicleSummaryRequest.response = payload
    },
    RESET_MUTATE_TRIP (state) {
      state.createTripRequest.status = LoadState.None
      state.createTripRequest.response = []

      state.updateTripRequest.status = LoadState.None
      state.updateTripRequest.response = []

      state.deleteTripRequest.status = LoadState.None
      state.deleteTripRequest.response = []
    },
    RESET_EXPORT_TRIP (state) {
      state.exportTripsRequest.status = LoadState.None
      state.exportTripsRequest.response = []
    },
    RESET_IMPORT_TRIP (state) {
      state.importTripsRequest.status = LoadState.None
      state.importTripsRequest.response = []
    },
    RESET_TRIP_ERRORS (state) {
      state.getTripErrorsRequest.status = LoadState.None
      state.getTripErrorsRequest.response = []
    },
    RESET_SUGGESTED_FUEL_STOPS (state) {
      state.getSuggestedFuelStopsRequest.status = LoadState.None
      state.getSuggestedFuelStopsRequest.response = []
    },
    IMPORT_TRIPS_REQUEST (state) {
      state.importTripsRequest.status = LoadState.Loading
    },
    IMPORT_TRIPS_RESPONSE (state, payload) {
      state.importTripsRequest.status = LoadState.Success
      state.importTripsRequest.response = payload
    },
    IMPORT_TRIPS_ERROR_RESPONSE (state, payload) {
      state.importTripsRequest.status = LoadState.Error
      state.importTripsRequest.response = payload
    },
    PILOT_DELETED (state, { id }) {
      state.getTripsRequest.trips = state.getTripsRequest.trips.map((trip) => {
        if (trip.pilot && trip.pilot.id === id) {
          return { ...trip, pilot: null }
        }
        return trip
      })
    },
    TAG_DELETED (state, { id }) {
      state.getTripsRequest.trips = state.getTripsRequest.trips.map((trip) => {
        if (trip.tag && trip.tag.id === id) {
          return { ...trip, tag: null }
        }
        return trip
      })
    },
    PILOT_UPDATED (state, { pilot }) {
      state.getTripsRequest.trips = state.getTripsRequest.trips.map((trip) => {
        if (trip.pilot && trip.pilot.id === pilot.id) {
          return { ...trip, pilot }
        }
        return trip
      })
    },
    TAG_UPDATED (state, { tag }) {
      state.getTripsRequest.trips = state.getTripsRequest.trips.map((trip) => {
        if (trip.tag && trip.tag.id === tag.id) {
          return { ...trip, tag }
        }
        return trip
      })
    },
    UPDATE_TRIP_ZONES (state, { zones }) {
      if (state.getSingleTripRequest.status === LoadState.Success) {
        if (state.getSingleTripRequest.response.startInfo) {
          state.getSingleTripRequest.response.startInfo.zone = findClosestZoneContainingLocation(zones, state.getSingleTripRequest.response.startInfo.location)
        }
        if (state.getSingleTripRequest.response.endInfo) {
          state.getSingleTripRequest.response.endInfo.zone = findClosestZoneContainingLocation(zones, state.getSingleTripRequest.response.endInfo.location)
        }
      }
      state.getTripsRequest.trips = state.getTripsRequest.trips.map((trip) => {
        if (trip.startInfo) {
          trip.startInfo.zone = findClosestZoneContainingLocation(zones, trip.startInfo.location)
        }
        if (trip.endInfo) {
          trip.endInfo.zone = findClosestZoneContainingLocation(zones, trip.endInfo.location)
        }
        return trip
      })
    },
    GET_FUEL_STOP_REQUEST (state) {
      state.getFuelStopRequest.status = LoadState.Loading
    },
    GET_FUEL_STOP_RESPONSE (state, payload) {
      state.getFuelStopRequest.status = LoadState.Success
      state.getFuelStopRequest.response = payload
    },
    GET_FUEL_STOP_ERROR_RESPONSE (state, payload) {
      state.getFuelStopRequest.status = LoadState.Error
      state.getFuelStopRequest.response = payload
    },
    CREATE_FUEL_STOP_REQUEST (state) {
      state.createFuelStopRequest.status = LoadState.Loading
    },
    CREATE_FUEL_STOP_RESPONSE (state, payload: FuelStop) {
      state.createFuelStopRequest.status = LoadState.Success
      state.createFuelStopRequest.response = payload

      const associatedTrip = state.getTripsRequest.trips.find(t => t.id === payload.tripId)

      if (associatedTrip) {
        const tripWithFuelStop = putFuelStop(associatedTrip, payload)
        state.getTripsRequest.trips = putTrip(state.getTripsRequest.trips, tripWithFuelStop)
        updateTripEdges(state, tripWithFuelStop)
      }
    },
    CREATE_FUEL_STOP_ERROR_RESPONSE (state, payload) {
      state.createFuelStopRequest.status = LoadState.Error
      state.createFuelStopRequest.response = payload
    },
    UPDATE_FUEL_STOP_REQUEST (state) {
      state.updateFuelStopRequest.status = LoadState.Loading
    },
    UPDATE_FUEL_STOP_RESPONSE (state, payload: FuelStop) {
      state.updateFuelStopRequest.status = LoadState.Success
      state.updateFuelStopRequest.response = payload

      const associatedTrip = state.getTripsRequest.trips.find(t => t.id === payload.tripId)
      const previouslyAssociatedTrip = state.getTripsRequest.trips.find(
        t => t.fuelStops.some(fs => fs.id === payload.id)
      )

      if (associatedTrip) {
        if (previouslyAssociatedTrip && previouslyAssociatedTrip.id !== associatedTrip.id) {
          const tripWithoutFuelstop = removeFuelStop(previouslyAssociatedTrip, payload)
          state.getTripsRequest.trips = putTrip(state.getTripsRequest.trips, tripWithoutFuelstop)
        }

        const tripWithUpdatedFuelStop = putFuelStop(associatedTrip, payload)
        state.getTripsRequest.trips = putTrip(state.getTripsRequest.trips, tripWithUpdatedFuelStop)
        updateTripEdges(state, tripWithUpdatedFuelStop)
      }
    },
    UPDATE_FUEL_STOP_ERROR_RESPONSE (state, payload) {
      state.updateFuelStopRequest.status = LoadState.Error
      state.updateFuelStopRequest.response = payload
    },
    DELETE_FUEL_STOP_REQUEST (state) {
      state.deleteFuelStopRequest.status = LoadState.Loading
    },
    DELETE_FUEL_STOP_RESPONSE (state, deletedId) {
      state.deleteFuelStopRequest.status = LoadState.Success
      state.deleteFuelStopRequest.response = deletedId

      const associatedTrip = state.getTripsRequest.trips.find(t => t.fuelStops.some(fs => fs.id === deletedId))

      if (associatedTrip) {
        const fuelStopsWithoutDeletedOne = associatedTrip.fuelStops.filter(fs => fs.id !== deletedId)

        const updatedTrip = { ...associatedTrip, fuelStops: fuelStopsWithoutDeletedOne }
        state.getTripsRequest.trips = putTrip(state.getTripsRequest.trips, updatedTrip)
        updateTripEdges(state, updatedTrip)
      }
    },
    DELETE_FUEL_STOP_ERROR_RESPONSE (state, payload) {
      state.deleteFuelStopRequest.status = LoadState.Error
      state.deleteFuelStopRequest.response = payload
    },
    GET_DEVICE_REQUEST (state) {
      state.getDeviceRequest.status = LoadState.Loading
    },
    GET_DEVICE_RESPONSE (state, payload) {
      state.getDeviceRequest.status = LoadState.Success
      state.getDeviceRequest.response = payload
    },
    GET_DEVICE_ERROR_RESPONSE (state, payload) {
      state.getDeviceRequest.status = LoadState.Error
      state.getDeviceRequest.response = payload
    },
    GET_FITMENT_REQUEST (state) {
      state.getFitmentRequest.status = LoadState.Loading
    },
    GET_FITMENT_RESPONSE (state, payload) {
      state.getFitmentRequest.status = LoadState.Success
      state.getFitmentRequest.response = payload
    },
    GET_FITMENT_ERROR_RESPONSE (state, payload) {
      state.getFitmentRequest.status = LoadState.Error
      state.getFitmentRequest.response = payload
    },
    UPDATE_VEHICLE_SETUP_REQUEST (state) {
      state.updateVehicleSetupRequest.status = LoadState.Loading
    },
    UPDATE_VEHICLE_SETUP_RESPONSE (state, vehicleId) {
      state.updateVehicleSetupRequest.status = LoadState.Success
      state.updateVehicleSetupRequest.response = null

      state.vehiclesRequest.response = state.vehiclesRequest.response
        .map(v => v.vehicle.id === vehicleId ? { ...v, vehicle: { ...v.vehicle, setupDone: true } } : v)

      if (state.sleectedTriplog && state.selectedTriplog.vehicle.id === vehicleId) {
        const st = state.selectedTriplog
        state.selectedTriplog = { ...st, vehicle: { ...st.vehicle, setupDone: true } }
      }
    },
    UPDATE_VEHICLE_SETUP_ERROR_RESPONSE (state, payload) {
      state.updateVehicleSetupRequest.status = LoadState.Error
      state.updateVehicleSetupRequest.response = payload
    },
    GET_SMART_CONNECT_VEHICLES_REQUEST (state) {
      state.getSmartConnectVehiclesRequest.status = LoadState.Loading
    },
    GET_SMART_CONNECT_VEHICLES_RESPONSE (state, payload) {
      state.getSmartConnectVehiclesRequest.status = LoadState.Success
      state.getSmartConnectVehiclesRequest.response = payload
    },
    GET_SMART_CONNECT_VEHICLES_ERROR_RESPONSE (state, payload) {
      state.getSmartConnectVehiclesRequest.status = LoadState.Error
      state.getSmartConnectVehiclesRequest.response = payload
    },
    SET_SMART_CONNECT_ODOMETER (state, payload) {
      state.getSmartConnectVehiclesRequest.response = state.getSmartConnectVehiclesRequest.response
        .map(car => {
          if (car.vehicleId === payload.vehicleId) {
            return { ...car, odometer: payload.odometer }
          } else {
            return car
          }
        })
    },
    SET_SEED_ODOMETER_REQUEST (state) {
      state.setSeedOdometerRequest.status = LoadState.Loading
    },
    SET_SEED_ODOMETER_RESPONSE (state, payload) {
      state.setSeedOdometerRequest.status = LoadState.Success
      state.setSeedOdometerRequest.response = payload
    },
    SET_SEED_ODOMETER_ERROR_RESPONSE (state, payload) {
      state.setSeedOdometerRequest.status = LoadState.Error
      state.setSeedOdometerRequest.response = payload
    },
    SET_FUEL_CONSUMPTION_TYPE_REQUEST (state) {
      state.setFuelConsumptionTypeRequest.status = LoadState.Loading
    },
    SET_FUEL_CONSUMPTION_TYPE_RESPONSE (state, payload) {
      state.setFuelConsumptionTypeRequest.status = LoadState.Success
      state.setFuelConsumptionTypeRequest.response = payload
    },
    SET_FUEL_CONSUMPTION_TYPE_ERROR_RESPONSE (state, payload) {
      state.setFuelConsumptionTypeRequest.status = LoadState.Error
      state.setFuelConsumptionTypeRequest.response = payload
    },
    SET_FUEL_UNIT_REQUEST (state) {
      state.setFuelUnitRequest.status = LoadState.Loading
    },
    SET_FUEL_UNIT_RESPONSE (state, payload) {
      state.setFuelUnitRequest.status = LoadState.Success
      state.setFuelUnitRequest.response = payload
    },
    SET_FUEL_UNIT_ERROR_RESPONSE (state, payload) {
      state.setFuelUnitRequest.status = LoadState.Error
      state.setFuelUnitRequest.response = payload
    },
    SET_MERGE_BREAK_THRESHOLD_REQUEST (state) {
      state.setMergeBreakThresholdRequest.status = LoadState.Loading
    },
    SET_MERGE_BREAK_THRESHOLD_RESPONSE (state, payload) {
      state.setMergeBreakThresholdRequest.status = LoadState.Success
      state.setMergeBreakThresholdRequest.response = payload
    },
    SET_MERGE_BREAK_THRESHOLD_ERROR_RESPONSE (state, payload) {
      state.setMergeBreakThresholdRequest.status = LoadState.Error
      state.setMergeBreakThresholdRequest.response = payload
    },
    SET_FUEL_CAPACITY_REQUEST (state) {
      state.setFuelCapacityRequest.status = LoadState.Loading
    },
    SET_FUEL_CAPACITY_RESPONSE (state, payload) {
      state.setFuelCapacityRequest.status = LoadState.Success
      state.setFuelCapacityRequest.response = payload
    },
    SET_FUEL_CAPACITY_ERROR_RESPONSE (state, payload) {
      state.setFuelCapacityRequest.status = LoadState.Error
      state.setFuelCapacityRequest.response = payload
    },
    SET_FUEL_STOP_SUGGESTIONS_REQUEST (state) {
      state.setFuelStopSuggestionsRequest.status = LoadState.Loading
    },
    SET_FUEL_STOP_SUGGESTIONS_RESPONSE (state, payload) {
      state.setFuelStopSuggestionsRequest.status = LoadState.Success
      state.setFuelStopSuggestionsRequest.response = payload
    },
    SET_FUEL_STOP_SUGGESTIONS_ERROR_RESPONSE (state, payload) {
      state.setFuelStopSuggestionsRequest.status = LoadState.Error
      state.setFuelStopSuggestionsRequest.response = payload
    },
    GET_SUGGESTED_FUEL_STOPS_REQUEST (state) {
      state.getSuggestedFuelStopsRequest.status = LoadState.Loading
    },
    APPEND_SUGGESTED_FUEL_STOPS_RESPONSE (state, payload) {
      state.getSuggestedFuelStopsRequest.status = LoadState.Success
      Array.isArray(state.getSuggestedFuelStopsRequest.response)
        ? state.getSuggestedFuelStopsRequest.response.push(...payload)
        : state.getSuggestedFuelStopsRequest.response = payload
    },
    GET_SUGGESTED_FUEL_STOPS_RESPONSE (state, payload) {
      state.getSuggestedFuelStopsRequest.status = LoadState.Success
      state.getSuggestedFuelStopsRequest.response = payload
    },
    GET_SUGGESTED_FUEL_STOPS_ERROR_RESPONSE (state, payload) {
      state.getSuggestedFuelStopsRequest.status = LoadState.Error
      state.getSuggestedFuelStopsRequest.response = payload
    },
    HIDE_SUGGESTED_FUEL_STOP_REQUEST (state) {
      state.hideSuggestedFuelStop.status = LoadState.Loading
    },
    HIDE_SUGGESTED_FUEL_STOP_RESPONSE (state, payload) {
      state.hideSuggestedFuelStop.status = LoadState.Success
      state.hideSuggestedFuelStop.response = payload
    },
    HIDE_SUGGESTED_FUEL_STOP_ERROR_RESPONSE (state, payload) {
      state.hideSuggestedFuelStop.status = LoadState.Error
      state.hideSuggestedFuelStop.response = payload
    },
    SHOW_ALL_SUGGESTED_FUEL_STOPS_REQUEST (state) {
      state.showAllSuggestedFuelStops.status = LoadState.Loading
    },
    SHOW_ALL_SUGGESTED_FUEL_STOPS_RESPONSE (state, payload) {
      state.showAllSuggestedFuelStops.status = LoadState.Success
      state.showAllSuggestedFuelStops.response = payload
    },
    SHOW_ALL_SUGGESTED_FUEL_STOPS_ERROR_RESPONSE (state, payload) {
      state.showAllSuggestedFuelStops.status = LoadState.Error
      state.showAllSuggestedFuelStops.response = payload
    },
    GET_ECO_SCORE_RECOMMENDATION_REQUEST (state) {
      state.getEcoScoreRecommendationRequest.status = LoadState.Loading
      state.getEcoScoreRecommendationRequest.response = {}
    },
    GET_ECO_SCORE_RECOMMENDATION_RESPONSE (state, payload) {
      state.getEcoScoreRecommendationRequest.status = LoadState.Success
      state.getEcoScoreRecommendationRequest.response = payload
    },
    GET_ECO_SCORE_RECOMMENDATION_ERROR_RESPONSE (state, payload) {
      state.getEcoScoreRecommendationRequest.status = LoadState.Error
      state.getEcoScoreRecommendationRequest.response = payload
    },
    RESET (state, property) {
      state[property].status = LoadState.None
      state[property].response = undefined
    },
  },

  actions: {
    selectTriplog ({ commit, getters }, { triplog }) {
      const triplogChanged = getters.getSelectedTriplog && triplog && getters.getSelectedTriplog.vehicle.id !== triplog.vehicle.id ||
        getters.getSelectedTriplog && !triplog ||
        !getters.getSelectedTriplog && triplog

      commit('SELECT_TRIPLOG', triplog)

      if (triplogChanged) {
        commit('RESET_GET_TRIPS')
      }
    },
    fetchVehicles ({ commit, getters }) {
      if (getters.getVehiclesStatus === LoadState.Success) {
        return getters.getVehicles
      }

      commit('VEHICLES_REQUEST')
      return new Promise((resolve, reject) => {
        triplogApiClient
          .tripLogsGet()
          .then((response) => {
            commit('VEHICLES_RESPONSE', response.data)
            resolve(response.data)
          })
          .catch((error) => {
            commit('VEHICLES_ERROR_RESPONSE', error.response)
            reject(error)
          })
      })
    },
    fetchSmartConnectVehicles ({ commit, getters }) {
      if (getters.getSmartConnectVehiclesStatus === LoadState.Success) {
        return getters.getSmartConnectVehicles
      }

      commit('GET_SMART_CONNECT_VEHICLES_REQUEST')
      return new Promise((resolve, reject) => {
        createVehicleAPIClient()
          .getSmartConnectVehicles()
          .then((response) => {
            commit('GET_SMART_CONNECT_VEHICLES_RESPONSE', response.data)
            resolve(response.data)
          })
          .catch((error) => {
            commit('GET_SMART_CONNECT_VEHICLES_ERROR_RESPONSE', error.response)
            reject(error)
          })
      })
    },
    setSeedOdometer ({ commit, getters, dispatch }, { vehicleId, seedOdometer }) {
      commit('SET_SEED_ODOMETER_REQUEST')
      return new Promise((resolve, reject) => {
        createVehicleAPIClient()
          .setSeedOdometer(vehicleId, { data: { seedOdometer } })
          .then((response) => {
            if (getters.getSmartConnectVehiclesStatus === LoadState.Success) {
              commit('SET_SMART_CONNECT_ODOMETER', { vehicleId, odometer: seedOdometer })
            }

            commit('SET_SEED_ODOMETER_RESPONSE', response.data)
            resolve(response.data)
          })
          .catch((error) => {
            commit('SET_SEED_ODOMETER_ERROR_RESPONSE', error.response)
            reject(error)
          })
      })
    },
    setFuelConsumptionType ({ commit, getters, dispatch }, { vehicleId, fuelConsumptionType }) {
      commit('SET_FUEL_CONSUMPTION_TYPE_REQUEST')
      return new Promise((resolve, reject) => {
        createVehicleAPIClient()
          .setFuelConsumptionType(vehicleId, { fuelConsumptionType })
          .then((response) => {
            if (getters.getVehiclesStatus === LoadState.Success) {
              commit('SET_TRIPLOG_VEHICLE_SETTINGS_PROPERTY', { vehicleId, fuelConsumptionType, name: 'fuelConsumptionType' })
            }

            commit('SET_FUEL_CONSUMPTION_TYPE_RESPONSE', response.data)
            resolve(response.data)
          })
          .catch((error) => {
            commit('SET_FUEL_CONSUMPTION_TYPE_ERROR_RESPONSE', error.response)
            reject(error)
          })
      })
    },
    setFuelUnit ({ commit, getters, dispatch }, { vehicleId, fuelUnit }) {
      commit('SET_FUEL_UNIT_REQUEST')
      return new Promise((resolve, reject) => {
        createVehicleAPIClient()
          .setFuelUnit(vehicleId, { fuelUnit })
          .then((response) => {
            if (getters.getVehiclesStatus === LoadState.Success) {
              commit('SET_TRIPLOG_VEHICLE_SETTINGS_PROPERTY', { vehicleId, fuelUnit, name: 'fuelUnit' })
            }

            commit('SET_FUEL_UNIT_RESPONSE', response.data)
            resolve(response.data)
          })
          .catch((error) => {
            commit('SET_FUEL_UNIT_ERROR_RESPONSE', error.response)
            reject(error)
          })
      })
    },
    setMergeBreakThreshold ({ commit, getters, dispatch }, { vehicleId, mergeBreakThreshold }) {
      commit('SET_MERGE_BREAK_THRESHOLD_REQUEST')
      return new Promise((resolve, reject) => {
        createVehicleAPIClient()
          .setMergeBreakThreshold(vehicleId, { mergeBreakThreshold })
          .then((response) => {
            if (getters.getVehiclesStatus === LoadState.Success) {
              commit('SET_TRIPLOG_VEHICLE_SETTINGS_PROPERTY', { vehicleId, mergeBreakThreshold, name: 'mergeBreakThreshold' })
            }

            commit('SET_MERGE_BREAK_THRESHOLD_RESPONSE', response.data)
            resolve(response.data)
          })
          .catch((error) => {
            commit('SET_MERGE_BREAK_THRESHOLD_ERROR_RESPONSE', error.response)
            reject(error)
          })
      })
    },
    setFuelCapacity ({ commit, getters, dispatch }, { vehicleId, fuelCapacity }) {
      commit('SET_FUEL_CAPACITY_REQUEST')
      return new Promise((resolve, reject) => {
        createVehicleAPIClient()
          .setFuelCapacity(vehicleId, { fuelCapacity })
          .then((response) => {
            if (getters.getVehiclesStatus === LoadState.Success) {
              commit('SET_TRIPLOG_VEHICLE_SETTINGS_PROPERTY', { vehicleId, fuelCapacity, name: 'fuelCapacity' })
            }

            commit('SET_FUEL_CAPACITY_RESPONSE', response.data)
            resolve(response.data)
          })
          .catch((error) => {
            commit('SET_FUEL_CAPACITY_ERROR_RESPONSE', error.response)
            reject(error)
          })
      })
    },
    setFuelStopSuggestions ({ commit, getters, dispatch }, { vehicleId, fuelStopSuggestions }) {
      commit('SET_FUEL_STOP_SUGGESTIONS_REQUEST')

      let previousValue

      if (getters.getVehiclesStatus === LoadState.Success) {
        const triplog = getters.getVehicles.find(v => v.vehicle.id === vehicleId)

        if (triplog) {
          previousValue = triplog.vehicleSettings.fuelStopSuggestions
          commit('SET_TRIPLOG_VEHICLE_SETTINGS_PROPERTY', { vehicleId, fuelStopSuggestions, name: 'fuelStopSuggestions' })
        }
      }

      return new Promise((resolve, reject) => {
        createVehicleAPIClient()
          .setFuelStopSuggestions(vehicleId, { fuelStopSuggestions })
          .then((response) => {
            commit('SET_FUEL_STOP_SUGGESTIONS_RESPONSE', response.data)
            resolve(response.data)
          })
          .catch((error) => {
            commit('SET_FUEL_STOP_SUGGESTIONS_ERROR_RESPONSE', error.response)

            if (previousValue) {
              commit('SET_TRIPLOG_VEHICLE_SETTINGS_PROPERTY', { vehicleId, fuelStopSuggestions: previousValue, name: 'fuelStopSuggestions' })
            }

            reject(error)
          })
      })
    },
    createTrip ({ commit, getters, dispatch }, { trip }) {
      commit('CREATE_TRIP_REQUEST')
      return new Promise((resolve, reject) => {
        createTripAPIClient()
          .createTrip(trip)
          .then((response) => {
            if (response['status'] >= 400) {
              commit('CREATE_TRIP_ERROR_RESPONSE', response.data)
              reject(response.data)
            } else {
              commit('CREATE_TRIP_RESPONSE', response)

              if (getters.getLatestTripStatus === LoadState.Success && getters.getLatestTrip && getters.getLatestTrip.fuelStops.length > 0) {
                dispatch('fetchSingleTrip', { tripId: getters.getLatestTrip.id, forceFetch: true })
              }

              if (getters.getTripsStatus === LoadState.Success) {
                commit('ADD_TRIP', response.data)
              }

              const from = findEarliestTripInList(getters.getTrips)?.startInfo?.timestamp
              const until = findLatestTripInList(getters.getTrips)?.endInfo?.timestamp

              dispatch('fetchTripErrors', { vehicleId: trip.vehicleId, from, until })

              dispatchResetStatistics(getters, dispatch, trip.vehicleId)
              dispatchResetCarHealth(dispatch)

              dispatch('fetchSuggestedFuelStops', { vehicleId: trip.vehicleId, from, until })

              resolve(response.data)
            }
          }).catch((error) => {
            commit('CREATE_TRIP_ERROR_RESPONSE', error)
            reject(error)
          })
      })
    },
    updateTrip ({ commit, getters, dispatch }, { tripId, trip, validateConflict = false }) {
      if (!Array.isArray(trip.stops)) {
        trip.stops = []
      }

      const cacheEndInfo = trip.endInfo
      const cachedEvents = trip.events
      if (trip.endInfo && (!trip.endInfo.timestamp || !trip.endInfo.address || !trip.endInfo.odometer)) {
        trip.endInfo = null
      }

      commit('UPDATE_TRIP_REQUEST')
      return new Promise((resolve, reject) => {
        createTripAPIClient()
          .updateTrip(tripId, trip, validateConflict)
          .then((response) => {
            if (response['status'] >= 400) {
              commit('UPDATE_TRIP_ERROR_RESPONSE', response.data)
              reject(response.data)
            } else {
              if (!response.data.endInfo) {
                response.data.endInfo = cacheEndInfo
                trip.endInfo = cacheEndInfo
              }

              (response.data as GetTrip).events = cachedEvents

              commit('UPDATE_TRIP_RESPONSE', response.data)

              const oldTrip: Trip = getters.getTrips.find(t => t.id === tripId)
              const fuelStopRelatedStartInfoChanged = oldTrip && (oldTrip.startInfo.timestamp !== trip.startInfo.timestamp || oldTrip.startInfo.odometer !== trip.startInfo.odometer)
              const fuelStopRelatedEndInfoChanged = oldTrip && oldTrip.endInfo && trip.endInfo && (oldTrip.endInfo.timestamp !== trip.endInfo.timestamp || oldTrip.endInfo.odometer !== trip.endInfo.odometer)

              if (getters.getTripsStatus === LoadState.Success && getters.getFuelStops.length > 0 && (fuelStopRelatedStartInfoChanged || fuelStopRelatedEndInfoChanged)) {
                dispatch('refreshTrips', { vehicleId: trip.vehicleId })
              }

              if (getters.getTripsStatus === LoadState.Success) {
                commit('REPLACE_TRIP', response.data)
              }

              const from = findEarliestTripInList(getters.getTrips)?.startInfo?.timestamp
              const until = findLatestTripInList(getters.getTrips)?.endInfo?.timestamp

              dispatch('fetchTripErrors', { vehicleId: trip.vehicleId, from, until })

              dispatchResetStatistics(getters, dispatch, trip.vehicleId)
              dispatchResetCarHealth(dispatch)

              dispatch('fetchSuggestedFuelStops', { vehicleId: trip.vehicleId, from, until })

              resolve(response.data)
            }
          }).catch((error) => {
            commit('UPDATE_TRIP_ERROR_RESPONSE', error)
            reject(error)
          })
      })
    },
    mergeTrips ({ commit, getters, dispatch }, { firstTripId, secondTripId, vehicleId }) {
      commit('MERGE_TRIP_REQUEST')

      return new Promise((resolve, reject) => {
        createTripAPIClient()
          .mergeTrip(vehicleId, { firstTripId, secondTripId })
          .then((response) => {
            commit('MERGE_TRIP_RESPONSE', { response: response.data, firstTripId, secondTripId })
            dispatchResetStatistics(getters, dispatch, vehicleId)
            resolve(response.data)
          }).catch((error) => {
            commit('MERGE_TRIP_ERROR_RESPONSE', error)
            reject(error)
          })
      })
    },
    deleteTrip ({ commit, getters, dispatch }, { trip }) {
      commit('DELETE_TRIP_REQUEST')
      return new Promise((resolve, reject) => {
        createTripAPIClient()
          .deleteTrip(trip.id)
          .then((response) => {
            commit('DELETE_TRIP_RESPONSE', response.data)

            if (getters.getTripsStatus === LoadState.Success && trip.fuelStops && trip.fuelStops.length > 0) {
              dispatch('refreshTrips', { vehicleId: trip.vehicleId })
            }

            if (getters.getTripsStatus === LoadState.Success) {
              commit('REMOVE_TRIP', trip.id)
            }

            if (getters.getLatestTrip && getters.getLatestTrip.id === trip.id) {
              dispatch('fetchLatestTrip', { vehicleId: getters.getLatestTrip.vehicleId, forceFetch: true })
            }

            if (getters.getOldestTrip && getters.getOldestTrip.id === trip.id) {
              dispatch('fetchOldestTrip', { vehicleId: getters.getOldestTrip.vehicleId, forceFetch: true })
            }

            dispatchResetStatistics(getters, dispatch, trip.vehicleId)
            dispatchResetCarHealth(dispatch)

            const from = findEarliestTripInList(getters.getTrips)?.startInfo?.timestamp
            const until = findLatestTripInList(getters.getTrips)?.endInfo?.timestamp

            dispatch('fetchSuggestedFuelStops', { vehicleId: trip.vehicleId, from, until })

            resolve(response.data)
          })
          .catch((error) => {
            commit('DELETE_TRIP_ERROR_RESPONSE', error.response)
            reject(error)
          })
      })
    },
    fetchSingleTrip ({ commit, getters }, { tripId, forceFetch = false }) {
      if (!forceFetch) {
        let trip

        if (getters.getLatestTrip && getters.getLatestTrip.id === tripId) {
          trip = getters.getLatestTrip
        } else if (getters.getOldestTrip && getters.getOldestTrip.id === tripId) {
          trip = getters.getOldestTrip
        } else {
          trip = getters.getTrips.find(trip => trip.id === tripId)
        }

        if (trip) {
          commit('GET_SINGLE_TRIP_RESPONSE', trip)

          // fetch previous and next asynchronously
          createTripAPIClient()
            .getTrip(tripId, ['previous', 'next'])
            .then((response) => {
              commit('GET_SINGLE_TRIP_RESPONSE', response.data)
            })
            .catch((error) => {
              commit('GET_SINGLE_TRIP_ERROR_RESPONSE', error.response)
            })

          return Promise.resolve(trip)
        }
      }

      commit('GET_SINGLE_TRIP_REQUEST')

      return new Promise((resolve, reject) => {
        createTripAPIClient()
          .getTrip(tripId, ['previous', 'next'])
          .then((response) => {
            commit('GET_SINGLE_TRIP_RESPONSE', response.data)
            resolve(response.data)
          })
          .catch((error) => {
            commit('GET_SINGLE_TRIP_ERROR_RESPONSE', error.response)
            reject(error)
          })
      })
    },
    fetchLatestTrip ({ commit, getters }, { vehicleId = null, forceFetch = false } = {}) {
      if (!forceFetch && getters.getLatestTrip && getters.getLatestTrip.vehicleId === vehicleId) {
        commit('GET_LATEST_TRIP_RESPONSE', getters.getLatestTrip)
        return Promise.resolve(getters.getLatestTrip)
      }

      commit('GET_LATEST_TRIP_REQUEST')

      return new Promise((resolve, reject) => {
        getEdgeTrip(vehicleId, 'latest')
          .then((response: AxiosResponse<TripsPage>) => {
            if (hasTriplogChanged(getters.getSelectedTriplog, vehicleId)) {
              reject(`trip selection has changed, discard fetched latest trip for ${vehicleId}`)
              return
            }

            commit('GET_LATEST_TRIP_RESPONSE', response.data.trips[0])
            resolve(response.data.trips[0])
          })
          .catch((error) => {
            commit('GET_LATEST_TRIP_ERROR_RESPONSE', error.response)
            reject(error)
          })
      })
    },
    fetchOldestTrip ({ commit, getters }, { vehicleId, forceFetch = false }) {
      if (!forceFetch && getters.getOldestTrip && getters.getOldestTrip.vehicleId === vehicleId) {
        commit('GET_OLDEST_TRIP_RESPONSE', getters.getOldestTrip)
        return Promise.resolve(getters.getOldestTrip)
      }

      commit('GET_OLDEST_TRIP_REQUEST')

      return new Promise((resolve, reject) => {
        getEdgeTrip(vehicleId, 'oldest')
          .then((response: AxiosResponse<TripsPage>) => {
            if (hasTriplogChanged(getters.getSelectedTriplog, vehicleId)) {
              reject(`trip selection has changed, discard fetched oldest trip for ${vehicleId}`)
              return
            }

            commit('GET_OLDEST_TRIP_RESPONSE', response.data.trips[0])
            resolve(response.data.trips[0])
          })
          .catch((error) => {
            commit('GET_OLDEST_TRIP_ERROR_RESPONSE', error.response)
            reject(error)
          })
      })
    },
    exportTrips ({ commit }, {
      vehicleId, email, from, until, tagIds, pilotIds, exportFormats,
    }) {
      commit('EXPORT_TRIPS_REQUEST')
      return new Promise((resolve, reject) => {
        createExportAPIClient()
          .exportTrips(vehicleId, {
            email,
            from,
            until,
            tagIds,
            pilotIds,
            exportFormats,
          })
          .then((response) => {
            commit('EXPORT_TRIPS_RESPONSE', response.data)
            resolve(response.data)
          })
          .catch((error) => {
            commit('EXPORT_TRIPS_ERROR_RESPONSE', error.response)
            reject(error)
          })
      })
    },
    fetchTripErrors ({ commit, getters }, { vehicleId, from, until, append = false }) {
      commit('GET_TRIP_ERRORS_REQUEST')
      return new Promise((resolve, reject) => {
        createTripAPIClient()
          .getTripErrors(vehicleId, from, until)
          .then((response) => {
            if (hasTriplogChanged(getters.getSelectedTriplog, vehicleId)) {
              commit('RESET', 'getTripErrorsRequest')
              reject(`trip selection has changed, discard fetched trip errors for ${vehicleId}`)
              return
            }
            if (append) {
              commit('APPEND_GET_TRIP_ERRORS_RESPONSE', response.data)
            } else {
              commit('GET_TRIP_ERRORS_RESPONSE', response.data)
            }
            resolve(response.data)
          })
          .catch((error) => {
            commit('GET_TRIP_ERRORS_ERROR_RESPONSE', error.response)
            reject(error)
          })
      })
    },
    fetchTrips ({ commit, getters }, { vehicleId }) {
      commit('SET_GET_TRIPS_STATUS', LoadState.Loading)
      return new Promise((resolve, reject) => {
        const filterOptions = getters.getFilterOptions
        createTripAPIClient().getTrips(
          vehicleId,
          filterOptions.date.from,
          filterOptions.date.until,
          filterOptions.tagId || undefined,
          filterOptions.pilotId || undefined,
          filterOptions.zoneId || undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          getters.getTripsOffset,
          getters.getTripsLimit,
        ).then((response: AxiosResponse<TripsPage>) => {
          if (hasTriplogChanged(getters.getSelectedTriplog, vehicleId)) {
            reject(`trip selection has changed, discard fetched trips for ${vehicleId}`)
            return
          }

          commit('NEXT_TRIP_REQUEST', {
            status: LoadState.Success,
            trips: response.data.trips,
            areMoreTripsAvailable: response.data.trips.length === getters.getTripsLimit,
          })
          resolve(response.data)
        })
          .catch((error) => {
            commit('SET_GET_TRIPS_STATUS', LoadState.Error)
            reject(error)
          })
      })
    },
    refreshTrips ({ commit, getters }, { vehicleId }) {
      const filterOptions = getters.getFilterOptions
      return new Promise((resolve, reject) => {
        createTripAPIClient().getTrips(
          vehicleId,
          filterOptions.date.from,
          filterOptions.date.until,
          filterOptions.tagId || undefined,
          filterOptions.pilotId || undefined,
          filterOptions.zoneId || undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          0,
          getters.getTripsOffset,
        ).then((response: AxiosResponse<TripsPage>) => {
          if (hasTriplogChanged(getters.getSelectedTriplog, vehicleId)) {
            reject(`trip selection has changed, discard refreshed trips for ${vehicleId}`)
            return
          }

          commit('SET_TRIPS', response.data.trips)
          resolve(response.data)
        }).catch((error) => {
          console.error('Error refreshing trips', error)
          reject(error)
        })
      })
    },
    fetchVehicleSummary ({ commit, getters }, { vehicleId, filterFrom, filterUntil, filterTagId, filterPilotId, filterZoneId }) {
      commit('GET_VEHICLE_SUMMARY_REQUEST')
      return new Promise((resolve, reject) => {
        createTripAPIClient()
          .getTripSummarized(vehicleId, filterFrom, filterUntil, filterTagId, filterPilotId, filterZoneId)
          .then((response) => {
            if (hasTriplogChanged(getters.getSelectedTriplog, vehicleId)) {
              reject(`trip selection has changed, discard fetched vehicle summary for ${vehicleId}`)
              return
            }

            commit('GET_VEHICLE_SUMMARY_RESPONSE', response.data)
            resolve(response.data)
          })
          .catch((error) => {
            commit('GET_VEHICLE_SUMMARY_ERROR_RESPONSE')
            reject(error)
          })
      })
    },
    importTrips ({ commit }, { csv, email }) {
      commit('IMPORT_TRIPS_REQUEST')
      return new Promise((resolve, reject) => {
        createImportAPIClient()
          .importTrips({ csv, email })
          .then((response) => {
            commit('IMPORT_TRIPS_RESPONSE', response.data)
            resolve(response.data)
          })
          .catch((error) => {
            commit('IMPORT_TRIPS_ERROR_RESPONSE', error.response)
            reject(error)
          })
      })
    },
    fetchFuelStop ({ commit, getters }, { vehicleId, id, forceFetch = false }) {
      if (!forceFetch && getters.getFuelStops.map(fs => fs.id).includes(id)) {
        const fuelStop = getters.getFuelStops.find(fs => fs.id === id)
        commit('GET_FUEL_STOP_RESPONSE', fuelStop)
        return Promise.resolve(fuelStop)
      }

      commit('GET_FUEL_STOP_REQUEST')

      return new Promise((resolve, reject) => {
        createFuelStopsAPIClient()
          .getFuelStop(vehicleId, id)
          .then((response) => {
            if (hasTriplogChanged(getters.getSelectedTriplog, vehicleId)) {
              reject(`trip selection has changed, discard fetched fuel stop for ${vehicleId}`)
              return
            }

            commit('GET_FUEL_STOP_RESPONSE', response.data)
            resolve(response.data)
          })
          .catch((error) => {
            commit('GET_FUEL_STOP_ERROR', error.response)
            reject(error)
          })
      })
    },
    fetchSuggestedFuelStops ({ commit, getters }, { vehicleId, from, until, append }) {
      if (getters.getVehiclesStatus === LoadState.Success) {
        const triplog = getters.getVehicles.find(v => v.vehicle.id === vehicleId)

        if (triplog && !triplog.vehicleSettings.fuelStopSuggestions) {
          commit('GET_SUGGESTED_FUEL_STOPS_RESPONSE', [])
          return Promise.resolve([])
        }
      }

      commit('GET_SUGGESTED_FUEL_STOPS_REQUEST')

      return new Promise((resolve, reject) => {
        createFuelStopsAPIClient()
          .getSuggestedFuelStops(vehicleId, from, until)
          .then((response) => {
            if (hasTriplogChanged(getters.getSelectedTriplog, vehicleId)) {
              reject(`trip selection has changed, discard fetched suggested fuel stop for ${vehicleId}`)
              return
            }

            if (append) {
              commit('APPEND_SUGGESTED_FUEL_STOPS_RESPONSE', response.data)
            } else {
              commit('GET_SUGGESTED_FUEL_STOPS_RESPONSE', response.data)
            }
            resolve(response.data)
          })
          .catch((error) => {
            commit('GET_SUGGESTED_FUEL_STOPS_ERROR_RESPONSE', error.response)
            reject(error)
          })
      })
    },
    createFuelStop ({ commit, getters, dispatch }, { vehicleId, fuelStopMutation }) {
      commit('CREATE_FUEL_STOP_REQUEST')
      return new Promise((resolve, reject) => {
        createFuelStopsAPIClient()
          .createFuelStop(vehicleId, fuelStopMutation)
          .then((response) => {
            // refetch trip with next fuel stop
            const fuelStop = response.data

            const tripsWithMoreRecentFuelStops = getters.getTrips
              .filter(t => t.fuelStops.length > 0)
              .filter(t => t.fuelStops.some(fs => fs.timestamp >= fuelStop.timestamp))

            const tripToRefetch = tripsWithMoreRecentFuelStops[tripsWithMoreRecentFuelStops.length - 1]

            if (tripToRefetch) {
              dispatch('fetchSingleTrip', { tripId: tripToRefetch.id, forceFetch: true })
            }

            const from = findEarliestTripInList(getters.getTrips)?.startInfo?.timestamp
            const until = findLatestTripInList(getters.getTrips)?.endInfo?.timestamp

            dispatch('fetchSuggestedFuelStops', { vehicleId, from, until })

            commit('CREATE_FUEL_STOP_RESPONSE', response.data)
            resolve(response.data)
          })
          .catch((error) => {
            commit('CREATE_FUEL_STOP_ERROR_RESPONSE', error.response)
            reject(error)
          })
      })
    },
    updateFuelStop ({ commit, getters, dispatch }, { vehicleId, id, fuelStopMutation }) {
      commit('UPDATE_FUEL_STOP_REQUEST')
      return new Promise((resolve, reject) => {
        createFuelStopsAPIClient()
          .updateFuelStop(vehicleId, id, fuelStopMutation)
          .then((response) => {
            commit('UPDATE_FUEL_STOP_RESPONSE', response.data)

            // if a fuel stop changes many associations may change
            // so we trigger trips refetch
            if (getters.getTripsStatus === LoadState.Success) {
              dispatch('refreshTrips', { vehicleId })
            }

            resolve(response.data)
          })
          .catch((error) => {
            commit('UPDATE_FUEL_STOP_ERROR_RESPONSE', error.response)
            reject(error)
          })
      })
    },
    deleteFuelStop ({ commit, getters, dispatch }, { vehicleId, id, fuelStop }) {
      commit('DELETE_FUEL_STOP_REQUEST')
      return new Promise((resolve, reject) => {
        createFuelStopsAPIClient()
          .deleteFuelStop(vehicleId, id)
          .then((response) => {
            commit('DELETE_FUEL_STOP_RESPONSE', id)

            const tripsWithMoreRecentFuelStops = getters.getTrips
              .filter(t => t.fuelStops.length > 0)
              .filter(t => t.fuelStops.some(fs => fs.timestamp >= fuelStop.timestamp))

            const tripToRefetch = tripsWithMoreRecentFuelStops[tripsWithMoreRecentFuelStops.length - 1]

            if (tripToRefetch) {
              dispatch('fetchSingleTrip', { tripId: tripToRefetch.id, forceFetch: true })
            }

            const from = findEarliestTripInList(getters.getTrips)?.startInfo?.timestamp
            const until = findLatestTripInList(getters.getTrips)?.endInfo?.timestamp

            dispatch('fetchSuggestedFuelStops', { vehicleId, from, until })

            resolve(response.data)
          })
          .catch((error) => {
            commit('DELETE_FUEL_STOP_ERROR_RESPONSE', error.response)
            reject(error)
          })
      })
    },
    zoneCreated ({ commit }, { zones }) {
      commit('UPDATE_TRIP_ZONES', { zones })
    },
    pilotDeleted ({ commit, getters }, { id }) {
      if (getters.getTripsStatus === LoadState.Success) {
        commit('PILOT_DELETED', { id })
      }
    },
    tagDeleted ({ commit, getters }, { id }) {
      if (getters.getTripsStatus === LoadState.Success) {
        commit('TAG_DELETED', { id })
      }
    },
    zoneDeleted ({ commit }, { zones }) {
      commit('UPDATE_TRIP_ZONES', { zones })
    },
    pilotUpdated ({ commit, getters }, { pilot }) {
      if (getters.getTripsStatus === LoadState.Success) {
        commit('PILOT_UPDATED', { pilot })
      }
    },
    tagUpdated ({ commit, getters }, { tag }) {
      if (getters.getTripsStatus === LoadState.Success) {
        commit('TAG_UPDATED', { tag })
      }
    },
    zoneUpdated ({ commit }, { zones }) {
      commit('UPDATE_TRIP_ZONES', { zones })
    },
    fetchDevice ({ commit, getters }, { deviceId }) {
      commit('GET_DEVICE_REQUEST')
      return new Promise((resolve, reject) => {
        createDeviceAPIClient()
          .getFullDevice(deviceId)
          .then((response) => {
            commit('GET_DEVICE_RESPONSE', response.data)
            resolve(response.data)
          })
          .catch((error) => {
            commit('GET_DEVICE_ERROR_RESPONSE', error.response)
            reject(error)
          })
      })
    },
    fetchFitment ({ commit }, { vehicle }) {
      commit('GET_FITMENT_REQUEST')
      return new Promise((resolve, reject) => {
        createFitmentAPIClient()
          .getFitment('oeamtc', vehicle.brand, vehicle.model, vehicle.year, 'lhd', 'de', 'trakm8_connect300')
          .then((response) => {
            commit('GET_FITMENT_RESPONSE', response.data)
            resolve(response.data)
          })
          .catch((error) => {
            commit('GET_FITMENT_ERROR_RESPONSE', error.response)
            reject(error)
          })
      })
    },
    updateVehicleSetup ({ commit }, { vehicleId }) {
      commit('UPDATE_VEHICLE_SETUP_REQUEST')
      return new Promise((resolve, reject) => {
        createVehicleAPIClient()
          .setSetup(vehicleId, { data: { done: true } })
          .then((response) => {
            commit('UPDATE_VEHICLE_SETUP_RESPONSE', vehicleId)
            resolve(response.data)
          })
          .catch((error) => {
            commit('UPDATE_VEHICLE_SETUP_ERROR_RESPONSE', error.response)
            reject(error)
          })
      })
    },
    hideSuggestedFuelStop ({ commit, dispatch, getters }, { vehicleId, timestamp }) {
      commit('HIDE_SUGGESTED_FUEL_STOP_REQUEST')
      console.log(timestamp)
      return new Promise((resolve, reject) => {
        createFuelStopsAPIClient()
          .hideSuggestedFuelStop(vehicleId, { timestamp })
          .then((response) => {
            const from = findEarliestTripInList(getters.getTrips)?.startInfo?.timestamp
            const until = findLatestTripInList(getters.getTrips)?.endInfo?.timestamp

            dispatch('fetchSuggestedFuelStops', { vehicleId, from, until })

            commit('HIDE_SUGGESTED_FUEL_STOP_RESPONSE', response.data)
            resolve(response.data)
          })
          .catch((error) => {
            commit('HIDE_SUGGESTED_FUEL_STOP_ERROR_RESPONSE', error.response)
            reject(error)
          })
      })
    },
    showAllSuggestedFuelStops ({ commit, dispatch, getters }, { vehicleId }) {
      commit('SHOW_ALL_SUGGESTED_FUEL_STOPS_REQUEST')
      return new Promise((resolve, reject) => {
        createFuelStopsAPIClient()
          .showAllSuggestedFuelStops(vehicleId)
          .then((response) => {
            const from = findEarliestTripInList(getters.getTrips)?.startInfo?.timestamp
            const until = findLatestTripInList(getters.getTrips)?.endInfo?.timestamp
            dispatch('fetchSuggestedFuelStops', { vehicleId, from, until })

            commit('SHOW_ALL_SUGGESTED_FUEL_STOPS_RESPONSE', response.data)
            resolve(response.data)
          })
          .catch((error) => {
            commit('SHOW_ALL_SUGGESTED_FUEL_STOPS_ERROR_RESPONSE', error.response)
            reject(error)
          })
      })
    },
    fetchEcoScoreRecommendation ({ commit }, { tripId }) {
      commit('GET_ECO_SCORE_RECOMMENDATION_REQUEST')
      return new Promise((resolve, reject) => {
        ecoScoreApiClient.getEcoScoreRecommendation(tripId)
          .then((response) => {
            commit('GET_ECO_SCORE_RECOMMENDATION_RESPONSE', response.data)
            resolve(response.data)
          })
          .catch((error) => {
            commit('GET_ECO_SCORE_RECOMMENDATION_ERROR_RESPONSE', error.response)
            reject(error)
          })
      })
    },
  },

  getters: {
    getSelectedTriplog: state => state.selectedTriplog,
    getVehiclesStatus: state => state.vehiclesRequest.status,
    isGetVehiclesSuccess: state => state.vehiclesRequest.status === LoadState.Success,
    isGetVehiclesError: state => state.vehiclesRequest.status === LoadState.Error || state.vehiclesRequest.status === LoadState.Unauthorized,
    getVehicles: state => state.vehiclesRequest.response,
    getCreateTripStatus: state => state.createTripRequest.status,
    getCreateTrip: state => state.createTripRequest.response,
    getUpdateTripStatus: state => state.updateTripRequest.status,
    getUpdateTrip: state => state.updateTripRequest.response,
    getMergeTripStatus: state => state.mergeTripRequest.status,
    getDeleteTripStatus: state => state.deleteTripRequest.status,
    getDeleteTrip: state => state.deleteTripRequest.response,
    getSingleTripStatus: state => state.getSingleTripRequest.status,
    getSingleTrip: state => state.getSingleTripRequest.response,
    getLatestTripStatus: state => state.getLatestTripRequest.status,
    getLatestTrip: state => state.getLatestTripRequest.response,
    getOldestTripStatus: state => state.getOldestTripRequest.status,
    getOldestTrip: state => state.getOldestTripRequest.response,
    getExportTripsStatus: state => state.exportTripsRequest.status,
    getExportTrips: state => state.exportTripsRequest.response,
    getTripErrorsStatus: state => state.getTripErrorsRequest.status,
    getTripErrors: state => state.getTripErrorsRequest.response,
    getFlatDistinctTripErrors: (state, getters) => {
      // flatten and remove duplicates, which may occur if single trip has multiple errors
      const errorsFlat = getters.getTripErrors?.flatMap(te => [te.first, te.second]) || []
      return errorsFlat.filter((terr, index, self) => index === self.findIndex(t => terr.id === t.id))
    },
    getTripsOffset: state => state.getTripsRequest.offset,
    getTripsLimit: state => state.getTripsRequest.limit,
    getTripsAreMoreAvailable: (state, getters) => !getters.getFilterOptions.errorsOnly && state.getTripsRequest.areMoreTripsAvailable,
    getFilterOptions: state => state.getTripsRequest.filterOptions,
    getTripsStatus: state => state.getTripsRequest.status,
    getVehicleSummaryStatus: state => state.getVehicleSummaryRequest.status,
    getVehicleSummary: state => state.getVehicleSummaryRequest.response,
    getLatestTripLocation: (state, getters) => {
      if (getters.getLatestTrip) {
        if (getters.getLatestTrip.endInfo && getters.getLatestTrip.endInfo.location) {
          return getters.getLatestTrip.endInfo.location
        } if (getters.getLatestTrip.startInfo) {
          return getters.getLatestTrip.startInfo.location
        }
      }
      return null
    },
    getLatestTripOdometer: (state, getters) => ((getters.getLatestTrip && getters.getLatestTrip.endInfo && getters.getLatestTrip.endInfo.odometer) ? getters.getLatestTrip.endInfo.odometer / 1000 : null),
    getLatestFilteredTrip: (state, getters) => getters.getTrips[0],
    getLatestFilteredLocation: (state, getters) => {
      if (getters.getLatestFilteredTrip) {
        if (getters.getLatestFilteredTrip.endInfo && getters.getLatestFilteredTrip.endInfo.location) {
          return getters.getLatestFilteredTrip.endInfo.location
        } if (getters.getLatestFilteredTrip.startInfo) {
          return getters.getLatestFilteredTrip.startInfo.location
        }
      }
      return null
    },
    getLatestFilteredInfos: (state, getters) => {
      if (getters.getLatestFilteredTrip) {
        return {
          start: getters.getLatestFilteredTrip.startInfo,
          end: getters.getLatestFilteredTrip.endInfo,
        }
      }
      return null
    },
    getLatestFilteredOdometer: (state, getters) => ((getters.getLatestFilteredTrip && getters.getLatestFilteredTrip.endInfo && getters.getLatestFilteredTrip.endInfo.odometer)
      ? getters.getLatestFilteredTrip.endInfo.odometer / 1000 : null),
    getTrips: (state, getters) => {
      if (getters.getFilterOptions.errorsOnly) {
        return getters.getFlatDistinctTripErrors
      }
      return state.getTripsRequest.trips || []
    },
    getImportTripsStatus: state => state.importTripsRequest.status,
    getImportTrips: state => state.importTripsRequest.response,
    getCreateFuelStopStatus: state => state.createFuelStopRequest.status,
    getFuelStops: (unused, getters) => {
      if (!getters.getTrips) {
        return []
      }

      return getters.getTrips
        .filter(t => t.fuelStops && t.fuelStops.length > 0)
        .flatMap(t => t.fuelStops)
    },
    getFuelStop: (state) => state.getFuelStopRequest.response,
    getFuelStopStatus: (state) => state.getFuelStopRequest.status,
    updateFuelStopStatus: (state) => state.updateFuelStopRequest.status,
    deleteFuelStopStatus: (state) => state.deleteFuelStopRequest.status,
    getDevice: (state) => state.getDeviceRequest.response,
    getDeviceStatus: (state) => state.getDeviceRequest.status,
    getFitment: (state) => {
      if (state.getFitmentRequest.response.results) {
        return state.getFitmentRequest.response.results[0]
      }
      return state.getFitmentRequest.response
    },
    getFitmentStatus: (state) => state.getFitmentRequest.status,
    getSmartConnectVehicles: (state) => state.getSmartConnectVehiclesRequest.response,
    getSmartConnectVehiclesStatus: (state) => state.getSmartConnectVehiclesRequest.status,
    getSeedOdometerUpdateStatus: (state) => state.setSeedOdometerRequest.status,
    getFuelConsumptionTypeUpdateStatus: (state) => state.setFuelConsumptionTypeRequest.status,
    getFuelUnit: (state) => {
      if (!state.selectedTriplog) {
        return 'Liter'
      }

      switch (state.selectedTriplog.vehicleSettings.fuelUnit) {
        case FuelUnit.LITER: return 'Liter'
        case FuelUnit.KWH: return 'kWh'
        case FuelUnit.KG: return 'kg'
      }
    },
    getSuggestedFuelStops: (state) => state.getSuggestedFuelStopsRequest.response,
    getSuggestedFuelStopsStatus: (state) => state.getSuggestedFuelStopsRequest.status,
    getEcoScoreRecommendation: (state) => state.getEcoScoreRecommendationRequest.response,
    getEcoScoreRecommendationStatus: (state) => state.getEcoScoreRecommendationRequest.status,
  },
}
